// ** React Imports
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

// ** UI Imports
import { Button, Select, SelectContent, SelectItem, SelectTrigger, Tooltip, TooltipContent, TooltipTrigger } from '@shopflo/ui'

// ** Third Party Imports
import { addDays, format, isSameDay, subDays } from 'date-fns'
import { DateRangePicker, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

//** Icon Imports
import { constants } from 'src/lib/utils/constants'
import {
  CompareToFilterOptionsType,
  FilterConfigType,
  FiltersType,
  StaticRangeType,
  TabType,
  SelectionRangeType,
  FilterParentType
} from 'src/lib/types/filters'
import { appliedDateFilter, getDateString, getDateTime, getEndDate, getStartDate, mapStaticRangeLabel } from 'src/lib/utils/helpers'
import { compareToFilterOptions, customCompareToFilterOptions } from 'src/assets/AnalyticsConfig'
import { OverviewWidgetFilterOptionsType } from 'src/lib/types/dashboardAnalytics'
import DatePickerDialog from './DatePickerDialog'
import { ArrowsOutLineHorizontal, CalendarDots, CaretDown } from '@phosphor-icons/react'

interface DateRangeType {
  dateFilter: FilterConfigType
  setDateFilterCtx: (filterObject: FilterConfigType) => void
  appliedFilter?: string
  minDateValue?: Date
  maxDateValue?: Date
  parentComponent: TabType
  comparison?: boolean
  hasCustomComparisons?: boolean
  triggerDatePicker?: boolean
  setTriggerDatePicker?: Dispatch<SetStateAction<boolean>>
  jobButtonText?: string
  excludeStaticRanges?: string[]
  restrictMonthNavigation?: boolean
}

const NewDatePicker: React.FC<DateRangeType> = ({
  dateFilter,
  setDateFilterCtx,
  appliedFilter,
  minDateValue,
  maxDateValue,
  parentComponent,
  comparison = false,
  hasCustomComparisons = false,
  triggerDatePicker,
  setTriggerDatePicker,
  jobButtonText = 'Apply',
  excludeStaticRanges,
  restrictMonthNavigation
}) => {
  const defaultRange = {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  }
  const value = globalThis?.window?.localStorage?.getItem(constants.FLO_FILTERS) ?? '{}'
  const dateFilters: FiltersType = JSON.parse(value)

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<SelectionRangeType>(defaultRange)

  const [currentCompareOption, setCurrentCompareOption] = useState<OverviewWidgetFilterOptionsType>(
    compareToFilterOptions[
      compareToFilterOptions.findIndex(
        (filterOption: OverviewWidgetFilterOptionsType) => filterOption?.value === dateFilter?.comparison
      )
    ]
  )
  const [showCompareDatePicker, setShowCompareDatePicker] = useState<boolean>(false)
  const [compareSelectOpen, setCompareSelectOpen] = useState<boolean>(false)
  const [compareDateRange, setCompareDateRange] = useState<SelectionRangeType>(defaultRange)
  const [selectedCompareStaticRange, setSelectedCompareStaticRange] = useState<StaticRangeType>('today')
  const [selectedStaticRange, setSelectedStaticRange] = useState<StaticRangeType>('today')

  useEffect(() => {
    if (currentCompareOption?.value === 'custom') {
      setShowCompareDatePicker(true)
    }
  }, [currentCompareOption])

  const setDateRangeFromFilter = () => {
    setDateRange({
      startDate: Boolean(dateFilter?.startDate)
        ? new Date(dateFilter?.startDate ?? '')
        : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
      endDate: Boolean(dateFilter?.endDate)
        ? addDays(new Date(dateFilter?.endDate ?? ''), -1)
        : new Date(
            addDays(new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)), 0)
          ),
      key: 'selection',
      compareToStartDate: Boolean(dateFilter?.compareToStartDate)
        ? new Date(dateFilter?.compareToStartDate ?? '')
        : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedCompareStaticRange)),
      compareToEndDate: Boolean(dateFilter?.compareToEndDate)
        ? new Date(dateFilter?.compareToEndDate ?? '')
        : new Date(
            addDays(
              new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedCompareStaticRange)),
              0
            )
          )
    })
  }

  useEffect(() => {
    if (!Boolean(dateFilters) || !appliedFilter?.length) {
      setDateRange(defaultRange)
      return
    }
    setDateRangeFromFilter()
  }, [showDatePicker])

  const handleSelect = (ranges: RangeKeyDict) => {
    const dateRangeObject = {
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
      key: ranges.selection.key
    }
    setDateRange(dateRangeObject)
  }

  const handleCompareSelect = (ranges: RangeKeyDict) => {
    const dateRangeObject = {
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
      key: ranges.selection.key
    }
    setCompareDateRange(dateRangeObject)
  }

  const handleCompareToOptionClick = (index: number) => {
    const comparisonObject = hasCustomComparisons ? customCompareToFilterOptions : compareToFilterOptions
    setCurrentCompareOption(comparisonObject[index])
    if (comparisonObject[index].value === 'custom') {
      setShowCompareDatePicker(true)
      return
    }
    setDateFilterCtx({
      ...dateFilter,
      comparison: comparisonObject[index].value as CompareToFilterOptionsType
    })
  }

  const handleApply = () => {
    const startDate = dateRange?.startDate ? getDateString(dateRange?.startDate, 0) : ''
    const endDate = dateRange?.endDate ? getDateString(dateRange?.endDate, 1) : ''
    const compareStartDate = compareDateRange?.startDate ? getDateString(compareDateRange?.startDate, 0) : ''
    const compareEndDate = compareDateRange?.endDate ? getDateString(compareDateRange?.endDate, 1) : ''
    setDateFilterCtx({
      ...dateFilter,
      selectedStaticRange: selectedStaticRange,
      startDate: startDate,
      endDate: endDate,
      ...(Boolean(currentCompareOption?.value === 'custom')
        ? {
            comparison: 'custom',
            selectedCompareStaticRange: selectedCompareStaticRange,
            compareToStartDate: compareStartDate,
            compareToEndDate: compareEndDate
          }
        : {}),
      lastUpdated: `${getDateTime(new Date()).date} at ${getDateTime(new Date()).time}`
    })
    setShowCompareDatePicker(false)
    setShowDatePicker(false)
    if (triggerDatePicker && setTriggerDatePicker) {
      setTriggerDatePicker(false)
    }
  }

  const isSelectedDateEqualToStaticRange = () => {
    const getSelectionStatus = (selectedFilter: FilterConfigType) => {
      return (
        (!Boolean(selectedFilter?.startDate)
          ? true
          : isSameDay(
              getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange),
              new Date(new Date(selectedFilter?.startDate ?? '').getTime() - 19800000)
            )) &&
        (!Boolean(selectedFilter?.endDate)
          ? true
          : isSameDay(
              getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange),
              new Date(new Date(selectedFilter?.endDate ?? '').getTime() - 19801000)
            ))
      )
    }
    return getSelectionStatus(dateFilter)
  }

  const selectedDropdownOption = useMemo(() => {
    return hasCustomComparisons
      ? customCompareToFilterOptions?.findIndex(
          (option: OverviewWidgetFilterOptionsType) => option?.value === dateFilter?.comparison
        ) === -1
        ? 0
        : customCompareToFilterOptions?.findIndex(
            (option: OverviewWidgetFilterOptionsType) => option?.value === dateFilter?.comparison
          )
      : compareToFilterOptions?.findIndex(
          (option: OverviewWidgetFilterOptionsType) => option?.value === dateFilter?.comparison
        ) === -1
      ? 0
      : compareToFilterOptions?.findIndex(
          (option: OverviewWidgetFilterOptionsType) => option?.value === dateFilter?.comparison
        )
  }, [dateFilter?.comparison, hasCustomComparisons])

  const comparisonOptions = useMemo(() => {
    const baseOptions = hasCustomComparisons ? customCompareToFilterOptions : compareToFilterOptions
    if (dateFilter?.comparison !== 'custom' || !dateFilter?.compareToStartDate || !dateFilter?.compareToEndDate) {
      return baseOptions
    }
    const start = new Date(dateFilter.compareToStartDate)
    const end = subDays(new Date(dateFilter.compareToEndDate), 1)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return baseOptions
    const rangeLabel = `${format(start, 'MMM d')} - ${format(end, "MMM d ''yy")}`
    return baseOptions.map((option: OverviewWidgetFilterOptionsType) =>
      option.value === 'custom' ? { ...option, label: rangeLabel } : option
    )
  }, [hasCustomComparisons, dateFilter?.comparison, dateFilter?.compareToStartDate, dateFilter?.compareToEndDate])

  return (
    <div className='flex items-center gap-1'>
      {Boolean(comparison) && (
        <Select
          open={compareSelectOpen}
          onOpenChange={setCompareSelectOpen}
          value={dateFilter?.comparison ?? ''}
          onValueChange={selectedValue => {
            const index = comparisonOptions.findIndex(
              (option: OverviewWidgetFilterOptionsType) => option.value === selectedValue
            )
            if (index >= 0) handleCompareToOptionClick(index)
          }}
        >
          <SelectTrigger renderType='basic' asChild>
            <Button
              variant='outline'
              size='sm'
              leftIcon={ArrowsOutLineHorizontal}
              rightIcon={CaretDown}
              className='whitespace-nowrap !bg-transparent !shadow-none !ring-0 hover:!bg-zinc-50'
              iconProps={{rightIconProps: {size: "xxs"}}}
            >
              {selectedDropdownOption === 0
                ? 'Compare'
                : `Compare: ${comparisonOptions[selectedDropdownOption]?.label ?? ''}`}
            </Button>
          </SelectTrigger>
          <SelectContent align='end'>
            {comparisonOptions.map((option: OverviewWidgetFilterOptionsType) => (
              <SelectItem
                key={option.value}
                value={option.value}
                onPointerDown={option.value === 'custom' ? () => {
                  setCurrentCompareOption(option as OverviewWidgetFilterOptionsType)
                  setShowCompareDatePicker(true)
                  setCompareSelectOpen(false)
                } : undefined}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            id='date-picker-range-button'
            variant='outline'
            size='sm'
            leftIcon={CalendarDots}
            rightIcon={CaretDown}
            onClick={() => setShowDatePicker(true)}
            className='whitespace-nowrap !bg-transparent !shadow-none !ring-0 hover:!bg-zinc-50'
            iconProps={{leftIconProps: {className: 'text-zinc-600'}, rightIconProps: {size: 'xxs', className: 'text-zinc-400'}}}
          >
            {isSelectedDateEqualToStaticRange()
              ? mapStaticRangeLabel(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)
              : appliedFilter}
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom'>{appliedDateFilter(dateFilter)}</TooltipContent>
      </Tooltip>

      {hasCustomComparisons && (
        <DatePickerDialog
          minDateValue={minDateValue}
          maxDateValue={maxDateValue}
          showDatePicker={showCompareDatePicker}
          setShowDatePicker={setShowCompareDatePicker}
          dateRange={compareDateRange}
          setSelectedStaticRange={setSelectedCompareStaticRange}
          handleSelect={handleCompareSelect}
          handleApply={handleApply}
          jobButtonText={jobButtonText}
          excludeStaticRanges={excludeStaticRanges}
          restrictMonthNavigation={restrictMonthNavigation}
        />
      )}

      <DatePickerDialog
        minDateValue={minDateValue}
        maxDateValue={maxDateValue}
        showDatePicker={triggerDatePicker ?? showDatePicker}
        setShowDatePicker={setTriggerDatePicker ?? setShowDatePicker}
        dateRange={dateRange}
        setSelectedStaticRange={setSelectedStaticRange}
        handleSelect={handleSelect}
        handleApply={handleApply}
        jobButtonText={jobButtonText}
        excludeStaticRanges={excludeStaticRanges}
        restrictMonthNavigation={restrictMonthNavigation}
      />
    </div>
  )
}

export default NewDatePicker
